/**
 * Phone number field: country dial code and national number in one control.
 *
 * The backend stores one international string and validates it, so the two
 * halves are only a convenience for entry - they are joined before they leave
 * this component, and split again when an existing number is loaded.
 */
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Popover,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  flagEmoji,
  formatNationalDisplay,
  nationalDigitsFromInput,
  nationalPlaceholder,
  isNationalNumberValid,
  isNationalNumberPossible,
  findCountryOption,
} from "../utils/phoneProfile";

export default function ProfilePhoneComboField({
  profileReady = true,
  countryCode,
  nationalDigits,
  options,
  onCountryChange,
  onNationalDigitsChange,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");

  const selected = useMemo(() => {
    return (
      findCountryOption(options, countryCode) ??
      findCountryOption(options, "DE") ??
      options[0] ?? { code: "DE", dial: "49", name: "Germany" }
    );
  }, [options, countryCode]);

  const displayNational = formatNationalDisplay(nationalDigits, selected.code);
  const placeholder = nationalPlaceholder(selected.code);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^\+/, "");
    if (!q) {
      return options;
    }
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.dial.includes(q) ||
        o.code.toLowerCase().includes(q)
    );
  }, [options, search]);

  const nat = String(nationalDigits || "").replace(/\D/g, "");
  const showError = nat.length > 0 && !isNationalNumberPossible(nationalDigits, selected.code);
  const showCheck =
    profileReady && isNationalNumberValid(nationalDigits, selected.code);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        component="label"
        htmlFor="profile-phone-national-input"
        variant="body2"
        color="text.secondary"
        sx={{
          display: "block",
          mb: 0.5,
          fontSize: "0.75rem",
          lineHeight: 1.4375,
        }}
      >
        Phone number
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          border: "1px solid",
          borderColor: showError ? "error.main" : "rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "background.paper",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
          "&:focus-within": {
            borderColor: showError ? "error.main" : "primary.main",
            boxShadow: showError
              ? undefined
              : (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
          },
        }}
      >
        <Button
          type="button"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            borderRadius: 0,
            px: 1.5,
            py: 1,
            minWidth: { xs: 118, sm: 136 },
            maxWidth: "42%",
            textTransform: "none",
            color: "text.primary",
            flexShrink: 0,
            justifyContent: "flex-start",
            gap: 0.75,
            alignSelf: "stretch",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <Box component="span" sx={{ fontSize: "1.25rem", lineHeight: 1, flexShrink: 0 }}>
            {flagEmoji(selected.code)}
          </Box>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ lineHeight: 1.2, textAlign: "left", flex: 1, minWidth: 0 }}
          >
            {selected.code} +{selected.dial}
          </Typography>
          <KeyboardArrowDownIcon sx={{ color: "text.secondary", fontSize: 20, flexShrink: 0 }} />
        </Button>

        <Divider orientation="vertical" flexItem sx={{ borderColor: "divider", alignSelf: "stretch" }} />

        <TextField
          id="profile-phone-national-input"
          name="phonenumber"
          hiddenLabel
          variant="outlined"
          error={showError}
          value={displayNational}
          placeholder={placeholder}
          onChange={(e) => onNationalDigitsChange(nationalDigitsFromInput(e.target.value))}
          inputProps={{
            inputMode: "tel",
            autoComplete: "tel-national",
            "aria-label": "Phone number (national)",
          }}
          InputProps={{
            endAdornment: showCheck ? (
              <InputAdornment position="end">
                <CheckCircleIcon sx={{ color: "success.main", fontSize: 22 }} aria-hidden />
              </InputAdornment>
            ) : null,
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              bgcolor: "transparent",
            },
            "& .MuiOutlinedInput-input": {
              py: 1.75,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
        />
      </Box>
      {showError ? (
        <FormHelperText error sx={{ mx: 1.75, mt: 0.5 }}>
          Invalid phone number for this country.
        </FormHelperText>
      ) : null}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setSearch("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          elevation: 8,
          sx: {
            width: { xs: "min(100vw - 24px, 380px)", sm: 380 },
            borderRadius: 2,
            mt: 1,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search for countries"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} fontSize="small" />,
            }}
            inputProps={{ autoComplete: "off" }}
            autoFocus
          />
        </Box>
        <List
          dense
          sx={{
            maxHeight: 280,
            overflow: "auto",
            py: 0,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          {filtered.map((o) => (
            <ListItemButton
              key={o.code}
              selected={o.code === selected.code}
              onClick={() => {
                onCountryChange(o.code);
                setAnchorEl(null);
                setSearch("");
              }}
              sx={{
                "&.Mui-selected": { backgroundColor: "action.selected" },
                "&.Mui-selected:hover": { backgroundColor: "action.selected" },
              }}
            >
              <Box component="span" sx={{ fontSize: "1.25rem", mr: 1.5, lineHeight: 1 }}>
                {flagEmoji(o.code)}
              </Box>
              <ListItemText
                primary={o.name}
                primaryTypographyProps={{ variant: "body2", noWrap: true }}
                sx={{ minWidth: 0, mr: 1 }}
              />
              <Chip
                label={`+${o.dial}`}
                size="small"
                sx={{ fontWeight: 600, bgcolor: "grey.100", flexShrink: 0 }}
              />
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </Box>
  );
}
